/**
 * Admin Groups Screen
 * 
 * Full group management with:
 * - CRUD operations for groups
 * - Hierarchical parent selection
 * - Member management
 * - Group-level role assignments
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  RefreshCw,
  X,
  Search,
  ChevronRight,
  Shield,
  UserPlus,
  UserMinus,
  Building2,
  Folder,
  Network,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { resolveAdminScope, fetchUsersForScope } from '../../../lib/adminScope';
import type { GroupType } from '../../../types/prioritization/permissions';
import type { OrgUnit } from '../../../types/prioritization/enterpriseIdentity';

// ============================================================================
// Types
// ============================================================================

interface GroupWithDetails {
  id: string;
  name: string;
  type: GroupType;
  description: string | null;
  parentGroupId: string | null;
  parentGroupName: string | null;
  memberCount: number;
  roleAssignmentCount: number;
  createdAt: string;
}

interface GroupMember {
  id: string; // membership ID
  userId: string;
  userName: string;
  userEmail: string;
  isPrimary: boolean;
  joinedAt: string;
}

interface GroupRoleAssignment {
  id: string;
  roleId: string;
  roleLabel: string;
  scopeKind: string;
  scopeId: string;
}

interface Role {
  id: string;
  label: string;
  permissionTier: number;
}

interface User {
  id: string;
  name: string;
  email: string;
}

// ============================================================================
// Constants
// ============================================================================

const GROUP_TYPE_CONFIG: Record<GroupType, { label: string; icon: typeof Building2; color: string }> = {
  organization: { label: 'Organization', icon: Building2, color: 'blue' },
  department: { label: 'Department', icon: Folder, color: 'purple' },
  team: { label: 'Team', icon: Users, color: 'green' },
  project: { label: 'Project', icon: Network, color: 'orange' },
  'interest-group': { label: 'Interest Group', icon: Users, color: 'pink' },
};

// ============================================================================
// Main Component
// ============================================================================

export default function AdminGroups() {
  const { switchRole, currentRole, assignedRoleNames } = useUserStore();
  const { orgUnits: orgUnitsMap, fetchFromSupabase: fetchOrgUnits } = useEnterpriseIdentityStore();
  const { currentWorkspaceId, currentTenantId, getCurrentWorkspace } = useTenantStore();
  const currentWorkspace = getCurrentWorkspace();
  const adminScope = useMemo(
    () => resolveAdminScope(currentRole, assignedRoleNames),
    [currentRole, assignedRoleNames],
  );

  // Convert org units Map to array
  const orgUnitsList = Array.from(orgUnitsMap.values());

  // Data state
  const [groups, setGroups] = useState<GroupWithDetails[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<GroupWithDetails | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);

  // ========================================================================
  // Data Fetching
  // ========================================================================

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('[AdminGroups] Starting data fetch, currentWorkspaceId:', currentWorkspaceId);
      const supabase = getSupabaseClient();

      const { currentRole: scopedActorRole, assignedRoleNames: scopedAssignments } = useUserStore.getState();
      const effectiveAdminScope = resolveAdminScope(scopedActorRole, scopedAssignments);


      // Build groups query - strict workspace filter so groups never cross tenants
      let groupsQuery = supabase.from('groups').select('*');
      let filterKind = 'none';
      if (currentWorkspaceId) {
        // Only groups in this workspace; no workspace_id.is.null to prevent cross-tenant visibility
        console.log('[AdminGroups] Filtering groups by workspace (strict):', currentWorkspaceId);
        groupsQuery = groupsQuery.eq('workspace_id', currentWorkspaceId);
        filterKind = 'strict_eq';
      } else {
        console.log('[AdminGroups] No workspace selected - showing no groups');
        groupsQuery = groupsQuery.eq('workspace_id', '00000000-0000-0000-0000-000000000000'); // match nothing
        filterKind = 'none';
      }

      // Fetch users based on admin scope
      const scopedUsersPromise = fetchUsersForScope(effectiveAdminScope, currentTenantId || undefined, currentWorkspaceId || undefined);
      
      // Build workspace-scoped queries for roles and role_assignments
      let rolesQuery = supabase.from('roles').select('*');
      if (currentWorkspaceId) {
        const tenantPart = currentTenantId ? `,workspace_id.eq.${currentTenantId}` : '';
        rolesQuery = rolesQuery.or(`is_system.eq.true,workspace_id.eq.${currentWorkspaceId},workspace_id.is.null${tenantPart}`);
      } else {
        rolesQuery = rolesQuery.eq('is_system', true);
      }

      // Group roles are stored with scope_id='*' (global), so always include that in the filter
      let raQuery = supabase.from('role_assignments').select('*').eq('subject_type', 'group');
      if (currentWorkspaceId && currentTenantId) {
        raQuery = raQuery.or(`scope_id.eq.${currentWorkspaceId},scope_id.eq.${currentTenantId},scope_id.eq.*`);
      } else if (currentWorkspaceId) {
        raQuery = raQuery.or(`scope_id.eq.${currentWorkspaceId},scope_id.eq.*`);
      } else if (currentTenantId) {
        raQuery = raQuery.or(`scope_id.eq.${currentTenantId},scope_id.eq.*`);
      } else {
        raQuery = raQuery.eq('scope_id', '*');
      }

      const [groupsResult, roleAssignmentsResult, rolesResult, scopedUsers] = await Promise.all([
        groupsQuery,
        raQuery,
        rolesQuery,
        scopedUsersPromise,
      ]);

      // Fetch memberships only for groups in this workspace (prevents cross-tenant leakage)
      const workspaceGroupIds = (groupsResult.data || []).map((g: { id: string }) => g.id);
      const membershipsResult = workspaceGroupIds.length > 0
        ? await supabase.from('memberships').select('*').in('group_id', workspaceGroupIds)
        : { data: [], error: null };

      console.log('[AdminGroups] Fetch complete:', {
        groups: groupsResult.data?.length || 0,
        groupsError: groupsResult.error?.message,
        memberships: membershipsResult.data?.length || 0,
        roles: rolesResult.data?.length || 0,
        users: scopedUsers?.length || 0,
        adminScope: effectiveAdminScope,
      });

      if (groupsResult.error) throw new Error(groupsResult.error.message);


      // Build lookup maps
      const groupLookup = new Map<string, any>();
      (groupsResult.data || []).forEach(g => groupLookup.set(g.id, g));

      // Count members per group
      const memberCounts = new Map<string, number>();
      (membershipsResult.data || []).forEach(m => {
        memberCounts.set(m.group_id, (memberCounts.get(m.group_id) || 0) + 1);
      });

      // Count role assignments per group
      const roleCounts = new Map<string, number>();
      (roleAssignmentsResult.data || []).forEach(ra => {
        roleCounts.set(ra.subject_id, (roleCounts.get(ra.subject_id) || 0) + 1);
      });

      // Build groups with details
      const groupsWithDetails: GroupWithDetails[] = (groupsResult.data || []).map(g => ({
        id: g.id,
        name: g.display_name || g.name,
        type: (g.type || 'team') as GroupType,
        description: g.description,
        parentGroupId: g.parent_id || g.parent_group_id,
        parentGroupName: (g.parent_id || g.parent_group_id) ? groupLookup.get(g.parent_id || g.parent_group_id)?.name : null,
        memberCount: memberCounts.get(g.id) || 0,
        roleAssignmentCount: roleCounts.get(g.id) || 0,
        createdAt: g.created_at,
      }));

      let roleRows = [...(rolesResult.data || [])];
      const seenRoleIds = new Set(roleRows.map((r: { id: string }) => r.id));
      const assignmentRoleIds = new Set(
        (roleAssignmentsResult.data || []).map((ra: { role_id: string }) => ra.role_id),
      );
      const missingRoleIds = [...assignmentRoleIds].filter((id) => id && !seenRoleIds.has(id));
      if (missingRoleIds.length > 0) {
        const { data: extraRoles } = await supabase.from('roles').select('*').in('id', missingRoleIds);
        for (const r of extraRoles || []) {
          roleRows.push(r);
          seenRoleIds.add(r.id);
        }
      }

      setGroups(groupsWithDetails);
      setRoles(roleRows.map(r => ({
        id: r.id,
        label: r.display_name || r.label || r.name,
        permissionTier: r.permission_tier || (r.tiers ? Math.max(...r.tiers) : 0),
      })));
      setAllUsers((scopedUsers || []).map(u => ({
        id: u.id,
        name: u.name,
        email: u.email,
      })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchOrgUnits(); // Load org units for scope selection
  }, [currentWorkspaceId, currentTenantId, assignedRoleNames]);

  // ========================================================================
  // CRUD Operations
  // ========================================================================

  const createGroup = async (data: { name: string; type: GroupType; description?: string; parentGroupId?: string }) => {
    try {
      const supabase = getSupabaseClient();
      const systemName = data.name.toLowerCase().replace(/\s+/g, '-');
      // Always scope new groups to current workspace so they never cross tenants (including for platform admin)
      const workspaceIdForInsert = currentWorkspaceId || null;
      if (!workspaceIdForInsert) {
        alert('Please select a workspace (tenant/workspace switcher) before creating a group.');
        return;
      }
      const { error } = await supabase.from('groups').insert({
        name: systemName,
        display_name: data.name,
        description: data.description || null,
        parent_id: data.parentGroupId || null,
        is_system: false,
        workspace_id: workspaceIdForInsert,
      });

      if (error) throw error;
      await fetchData();
      setShowCreateModal(false);
    } catch (err) {
      const msg = (err && typeof err === 'object' && 'message' in err)
        ? String((err as { message: string }).message)
        : (err instanceof Error ? err.message : 'Unknown error');
      const friendlyMessage = (msg.includes('groups_workspace_name_key') || msg.includes('groups_name_key') || msg.toLowerCase().includes('unique constraint'))
        ? 'A group with this name already exists in this workspace. Please choose a different name.'
        : msg;
      alert(`Failed to create group: ${friendlyMessage}`);
    }
  };

  const updateGroup = async (id: string, data: { name: string; type: GroupType; description?: string; parentGroupId?: string }) => {
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('groups').update({
        display_name: data.name,
        description: data.description || null,
        parent_id: data.parentGroupId || null,
      }).eq('id', id);

      if (error) throw error;
      await fetchData();
      setShowEditModal(false);
      setSelectedGroup(null);
    } catch (err) {
      alert(`Failed to update group: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const deleteGroup = async (id: string) => {
    if (!confirm('Delete this group? This will remove all memberships and role assignments.')) return;

    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('groups').delete().eq('id', id);
      if (error) throw error;
      await fetchData();
    } catch (err) {
      alert(`Failed to delete group: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // ========================================================================
  // Filtering
  // ========================================================================

  const filteredGroups = groups.filter(g => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return g.name.toLowerCase().includes(query) || g.type.toLowerCase().includes(query);
  });

  // ========================================================================
  // Permission Check — allow tenant-admin and workspace-admin (scoped); platform-admin (full)
  // ========================================================================

  if (adminScope === 'none') {
    return (
      <div className="p-8">
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4">
          <h2 className="text-lg font-semibold text-[var(--coral)] mb-2">Access Denied</h2>
          <p className="text-sm text-[var(--coral)] mb-4">
            You must be a Tenant Admin, Workspace Admin, or Platform Admin to access this page.
          </p>
          <button
            onClick={() => switchRole('platform-admin')}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
          >
            Switch to Platform Admin
          </button>
        </div>
      </div>
    );
  }

  // ========================================================================
  // Render
  // ========================================================================

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Users className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Groups"
        subtitle={
          <span>
            Manage organizational groups and their memberships
            {currentWorkspace && (
              <span className="ml-1.5 text-primary-600">({currentWorkspace.name})</span>
            )}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create Group
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-sm text-[var(--text-ter)]">Total Groups</div>
          <div className="text-2xl font-bold text-[var(--text-pri)]">{groups.length}</div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-sm text-[var(--text-ter)]">Teams</div>
          <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">
            {groups.filter(g => g.type === 'team').length}
          </div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-sm text-[var(--text-ter)]">Departments</div>
          <div className="text-2xl font-bold text-[var(--text-ter)]">
            {groups.filter(g => g.type === 'department').length}
          </div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-sm text-[var(--text-ter)]">Total Members</div>
          <div className="text-2xl font-bold text-[var(--text-ter)]">
            {groups.reduce((sum, g) => sum + g.memberCount, 0)}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[var(--text-ter)]" />
        <input
          type="text"
          placeholder="Search groups by name or type..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4">
          <p className="text-[var(--coral)]">{error}</p>
        </div>
      )}

      {/* Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-[var(--text-ter)] mr-2" />
            <span className="text-[var(--text-ter)]">Loading groups...</span>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="col-span-full text-center py-12 text-[var(--text-ter)]">
            {searchQuery ? 'No groups match your search.' : 'No groups found. Create one to get started.'}
          </div>
        ) : (
          filteredGroups.map((group) => {
            const typeConfig = GROUP_TYPE_CONFIG[group.type];
            const TypeIcon = typeConfig.icon;

            return (
              <div
                key={group.id}
                className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2  bg-${typeConfig.color}-100`}>
                      <TypeIcon className={`h-5 w-5 text-${typeConfig.color}-600`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[var(--text-pri)]">{group.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full bg-${typeConfig.color}-100 text-${typeConfig.color}-700`}>
                        {typeConfig.label}
                      </span>
                    </div>
                  </div>
                </div>

                {group.description && (
                  <p className="text-sm text-[var(--text-sec)] mb-3 line-clamp-2">{group.description}</p>
                )}

                {group.parentGroupName && (
                  <div className="text-xs text-[var(--text-ter)] mb-3 flex items-center gap-1">
                    <ChevronRight className="h-3 w-3" />
                    Parent: {group.parentGroupName}
                  </div>
                )}

                <div className="flex items-center gap-4 text-sm text-[var(--text-ter)] mb-4">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {group.memberCount} members
                  </div>
                  <div className="flex items-center gap-1">
                    <Shield className="h-4 w-4" />
                    {group.roleAssignmentCount} roles
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-[var(--rule)]">
                  <button
                    onClick={() => { setSelectedGroup(group); setShowMembersModal(true); }}
                    className="flex-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] py-1"
                  >
                    Members
                  </button>
                  <button
                    onClick={() => { setSelectedGroup(group); setShowRolesModal(true); }}
                    className="flex-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] py-1"
                  >
                    Roles
                  </button>
                  <button
                    onClick={() => { setSelectedGroup(group); setShowEditModal(true); }}
                    className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteGroup(group.id)}
                    className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <GroupFormModal
          title="Create Group"
          groups={groups}
          onSubmit={createGroup}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showEditModal && selectedGroup && (
        <GroupFormModal
          title="Edit Group"
          initialData={selectedGroup}
          groups={groups.filter(g => g.id !== selectedGroup.id)}
          onSubmit={(data) => updateGroup(selectedGroup.id, data)}
          onClose={() => { setShowEditModal(false); setSelectedGroup(null); }}
        />
      )}

      {showMembersModal && selectedGroup && (
        <GroupMembersModal
          group={selectedGroup}
          allUsers={allUsers}
          onClose={() => { setShowMembersModal(false); setSelectedGroup(null); }}
          onRefresh={fetchData}
        />
      )}

      {showRolesModal && selectedGroup && (
        <GroupRolesModal
          group={selectedGroup}
          roles={roles}
          orgUnits={orgUnitsList}
          onClose={() => { setShowRolesModal(false); setSelectedGroup(null); }}
          onRefresh={fetchData}
        />
      )}
    </div>
  );
}

// ============================================================================
// Group Form Modal
// ============================================================================

interface GroupFormModalProps {
  title: string;
  initialData?: GroupWithDetails;
  groups: GroupWithDetails[];
  onSubmit: (data: { name: string; type: GroupType; description?: string; parentGroupId?: string }) => void;
  onClose: () => void;
}

function GroupFormModal({ title, initialData, groups, onSubmit, onClose }: GroupFormModalProps) {
  const [name, setName] = useState(initialData?.name || '');
  const [type, setType] = useState<GroupType>(initialData?.type || 'team');
  const [description, setDescription] = useState(initialData?.description || '');
  const [parentGroupId, setParentGroupId] = useState(initialData?.parentGroupId || '');

  const handleSubmit = () => {
    if (!name.trim()) {
      alert('Name is required');
      return;
    }
    onSubmit({ name, type, description, parentGroupId: parentGroupId || undefined });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">{title}</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
              placeholder="Enter group name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as GroupType)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(GROUP_TYPE_CONFIG).map(([value, config]) => (
                <option key={value} value={value}>{config.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
              placeholder="Enter description (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Parent Group</label>
            <select
              value={parentGroupId}
              onChange={(e) => setParentGroupId(e.target.value)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No parent (top-level)</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
          >
            {initialData ? 'Update' : 'Create'} Group
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Group Members Modal
// ============================================================================

interface GroupMembersModalProps {
  group: GroupWithDetails;
  allUsers: User[];
  onClose: () => void;
  onRefresh: () => void;
}

function GroupMembersModal({ group, allUsers, onClose, onRefresh }: GroupMembersModalProps) {
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  useEffect(() => {
    loadMembers();
  }, [group.id]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const supabase = getSupabaseClient();
      
      // Fetch memberships and users separately to avoid FK cache issues
      const { data: membershipsData, error: membershipsError } = await supabase
        .from('memberships')
        .select('*')
        .eq('group_id', group.id);

      if (membershipsError) throw membershipsError;

      // Get unique user IDs
      const userIds = [...new Set((membershipsData || []).map(m => m.user_id))];
      
      // Fetch users
      let usersMap = new Map<string, { name: string; email: string }>();
      if (userIds.length > 0) {
        const { data: usersData } = await supabase
          .from('users')
          .select('id, name, email')
          .in('id', userIds);
        
        (usersData || []).forEach(u => {
          usersMap.set(u.id, { name: u.name, email: u.email });
        });
      }

      setMembers((membershipsData || []).map((m: any) => {
        const user = usersMap.get(m.user_id);
        return {
          id: m.id,
          userId: m.user_id,
          userName: user?.name || 'Unknown',
          userEmail: user?.email || '',
          isPrimary: m.is_primary,
          joinedAt: m.created_at,
        };
      }));
    } catch (err) {
      console.error('Failed to load members:', err);
    } finally {
      setLoading(false);
    }
  };

  const addMember = async () => {
    if (!selectedUserId) return;
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('memberships').insert({
        user_id: selectedUserId,
        group_id: group.id,
      });
      if (error) throw error;
      setSelectedUserId('');
      setShowAddUser(false);
      await loadMembers();
      onRefresh();
    } catch (err) {
      alert(`Failed to add member: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const removeMember = async (membershipId: string) => {
    if (!confirm('Remove this member?')) return;
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('memberships').delete().eq('id', membershipId);
      if (error) throw error;
      await loadMembers();
      onRefresh();
    } catch (err) {
      alert(`Failed to remove member: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const availableUsers = allUsers.filter(u => !members.some(m => m.userId === u.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">{group.name} - Members</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Add member section */}
        {showAddUser ? (
          <div className="mb-4 p-3 bg-[var(--surface)] flex gap-2">
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] flex-1 border border-[var(--rule)] px-3 py-2"
            >
              <option value="">Select user...</option>
              {availableUsers.map(user => (
                <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
              ))}
            </select>
            <button onClick={addMember} className="btn btn-primary">Add</button>
            <button onClick={() => setShowAddUser(false)} className="btn btn-secondary">Cancel</button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddUser(true)}
            className="mb-4 flex items-center gap-2 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)]"
          >
            <UserPlus className="h-4 w-4" /> Add Member
          </button>
        )}

        {/* Members list */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-[var(--text-ter)]">Loading members...</div>
          ) : members.length === 0 ? (
            <div className="text-center py-8 text-[var(--text-ter)]">No members in this group</div>
          ) : (
            <div className="space-y-2">
              {members.map(member => (
                <div key={member.id} className="flex items-center justify-between p-3 bg-[var(--surface)]">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-[var(--hover-tint)] flex items-center justify-center">
                      <span className="text-sm font-medium text-[var(--text-sec)]">
                        {member.userName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[var(--text-pri)]">{member.userName}</div>
                      <div className="text-xs text-[var(--text-ter)]">{member.userEmail}</div>
                    </div>
                    {member.isPrimary && (
                      <span className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] px-2 py-0.5 rounded">Primary</span>
                    )}
                  </div>
                  <button
                    onClick={() => removeMember(member.id)}
                    className="text-[var(--text-ter)] hover:text-[var(--coral)]"
                  >
                    <UserMinus className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Group Roles Modal
// ============================================================================

interface GroupRolesModalProps {
  group: GroupWithDetails;
  roles: Role[];
  orgUnits: OrgUnit[];
  onClose: () => void;
  onRefresh: () => void;
}

function GroupRolesModal({ group, roles, orgUnits: _orgUnits, onClose, onRefresh }: GroupRolesModalProps) {
  const [assignments, setAssignments] = useState<GroupRoleAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddRole, setShowAddRole] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');

  useEffect(() => {
    loadAssignments();
  }, [group.id]);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from('role_assignments')
        .select('*, roles:role_id(id, name, display_name)')
        .eq('subject_type', 'group')
        .eq('subject_id', group.id);

      if (error) throw error;

      setAssignments((data || []).map((ra: any) => ({
        id: ra.id,
        roleId: ra.role_id,
        roleLabel: ra.roles?.display_name || ra.roles?.name || ra.role_id,
        scopeKind: ra.scope_kind,
        scopeId: ra.scope_id,
      })));
    } catch (err) {
      console.error('Failed to load role assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  const addRole = async () => {
    if (!selectedRoleId) return;
    try {
      const supabase = getSupabaseClient();
      // Groups have roles globally - scope is determined by group placement at org units
      const { error } = await supabase.from('role_assignments').insert({
        subject_type: 'group',
        subject_id: group.id,
        role_id: selectedRoleId,
        scope_kind: 'global',
        scope_id: '*',
        granted_by: useUserStore.getState().userId,
      });
      if (error) throw error;
      setSelectedRoleId('');
      setShowAddRole(false);
      await loadAssignments();
      onRefresh();
    } catch (err) {
      alert(`Failed to assign role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const removeRole = async (assignmentId: string) => {
    if (!confirm('Remove this role assignment?')) return;
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('role_assignments').delete().eq('id', assignmentId);
      if (error) throw error;
      await loadAssignments();
      onRefresh();
    } catch (err) {
      alert(`Failed to remove role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">{group.name} - Role Assignments</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-[var(--text-sec)] mb-4">
          Roles assigned here are inherited by all members of this group.
        </p>

        {/* Add role section */}
        {showAddRole ? (
          <div className="mb-4 p-3 bg-[var(--surface)] space-y-3">
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2"
            >
              <option value="">Select role...</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>{role.label}</option>
              ))}
            </select>
            <p className="text-xs text-[var(--text-ter)]">
              This role defines what members of this group can do. Where they can do it is determined by placing the group at org units in the Identity page.
            </p>
            <div className="flex gap-2">
              <button onClick={addRole} disabled={!selectedRoleId} className="btn btn-primary disabled:opacity-50">Assign</button>
              <button onClick={() => setShowAddRole(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddRole(true)}
            className="mb-4 flex items-center gap-2 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)]"
          >
            <Shield className="h-4 w-4" /> Assign Role
          </button>
        )}

        {/* Assignments list */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-[var(--text-ter)]">Loading assignments...</div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-8 text-[var(--text-ter)]">No roles assigned to this group</div>
          ) : (
            <div className="space-y-2">
              {assignments.map(assignment => (
                <div key={assignment.id} className="flex items-center justify-between p-3 bg-[var(--surface)]">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-[var(--text-ter)]" />
                    <span className="text-sm font-medium text-[var(--text-pri)]">{assignment.roleLabel}</span>
                  </div>
                  <button
                    onClick={() => removeRole(assignment.id)}
                    className="text-[var(--text-ter)] hover:text-[var(--coral)]"
                    title="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}
